import { useState, useMemo, useRef } from "react";

import {
  Search,
  Grid,
  List,
  X,
  Sparkles,
  TrendingUp,
  Store,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProductCard, Product } from "@/components/business/products/ProductCard";

import { CartDrawer } from "@/components/business/cart/CartDrawer";
import { BackToDashboardButton } from "@/components/layout/BackToDashboardButton";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useDebounceValue } from "@/hooks/useDebounce";
import {
  usePOSList,
  useCategories,
  useWeeklyMenus,
  useProductsByPOS,
} from "@/hooks/useStoreData";
import { useProductCacheSync } from "@/hooks/useProductCacheSync";

type ViewMode = "grid" | "list";
type SortOption = "name" | "price-asc" | "price-desc" | "category";

// Imagen placeholder por defecto para menús y productos
const PLACEHOLDER_IMAGE = "/food-placeholder.jpg";

type WeeklyMenu = {
  id: string;
  fecha: string;
  nombre: string;
  descripcion?: string;
  imagen_url?: string | null;
};

function formatDate(date: Date) {
  // Usar fecha LOCAL para evitar desfase de zona horaria (ej. Chile UTC-4)
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Evita desfases de zona horaria al parsear YYYY-MM-DD
function parseYYYYMMDDToLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map((v) => Number(v));
  return new Date(y, (m || 1) - 1, d || 1);
}

function getCurrentWeekRange(today = new Date()) {
  const day = today.getDay(); // 0=Sun ... 6=Sat
  const mondayOffset = day === 0 ? -6 : 1 - day; // move to Monday
  const monday = new Date(today);
  monday.setDate(today.getDate() + mondayOffset);
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  return { start: formatDate(monday), end: formatDate(friday), monday };
}

function getWeekDates(monday: Date): string[] {
  const dates: string[] = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dates.push(formatDate(d));
  }
  return dates;
}

/**
 * Página principal de la tienda con diseño moderno y optimizaciones
 *
 * Muestra todos los productos disponibles con filtros, búsqueda
 * y opciones de vista. Optimizada con React Query, debounce y useMemo.
 */
export function StorePage() {
  // UI State
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedPos, setSelectedPos] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortOption>("name");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedMenuImage, setSelectedMenuImage] = useState<WeeklyMenu | null>(null);
  const weeklyScrollRef = useRef<HTMLDivElement | null>(null);

  // Auth
  const user = useAuth((s) => s.user);
  const isApoderado = user?.roles?.includes("Apoderado") ?? false;

  // Data fetching con React Query (caching automático)
  const { data: posList = [], isLoading: loadingPOS } = usePOSList();
  const { data: categoriesData } = useCategories();
  const categoryById = categoriesData?.categoryById ?? {};

  // Cargar productos del POS seleccionado
  const posId = selectedPos ? Number(selectedPos) : null;
  const { data: posProducts = [], isLoading: loadingProducts } = useProductsByPOS(
    posId,
    isApoderado
  );

  // 🔄 Sincronizar productos cargados al cache (evita requests individuales)
  useProductCacheSync(posProducts, categoryById, posId);

  // Cargar menús semanales
  const { start, end } = useMemo(() => getCurrentWeekRange(), []);
  const { data: weeklyMenusRaw = [], isLoading: loadingMenus } = useWeeklyMenus(start, end);

  // Mapear menús con placeholder
  const weeklyMenus: WeeklyMenu[] = useMemo(
    () =>
      weeklyMenusRaw.map((m) => ({
        id: m.id,
        fecha: m.fecha,
        nombre: m.nombre,
        descripcion: m.descripcion,
        imagen_url: m.imagen_url || PLACEHOLDER_IMAGE,
      })),
    [weeklyMenusRaw]
  );

  // Auto-seleccionar primer POS cuando cargue la lista
  useMemo(() => {
    if (!selectedPos && posList.length > 0) {
      setSelectedPos(String(posList[0].id));
    }
  }, [posList, selectedPos]);

  // UI Hooks
  const debouncedSearchTerm = useDebounceValue(searchTerm, 300);
  const showMenusHero = selectedPos
    ? posList.find((p) => String(p.id) === selectedPos)?.nombre.toLowerCase().includes("casino") ?? false
    : false;

  // Transformar de raw products a UI Products
  const products: Product[] = useMemo(() => {
    return posProducts.map((p) => ({
      id: p.id,
      name: p.nombre,
      description: p.descripcion,
      price: Number(p.precio),
      category: categoryById[p.category_id] ?? String(p.category_id),
      disponible: p.esta_disponible,
      stock: 0,
      image: p.imagen_url || PLACEHOLDER_IMAGE,
      rating: undefined,
      posId: posId || 0,
    }));
  }, [posProducts, categoryById, posId]);

  // Filtrar y ordenar productos (optimizado con useMemo)
  const filteredProducts = useMemo(() => {
    let filtered = products;

    // Filtrar por búsqueda (con debounce aplicado)
    if (debouncedSearchTerm) {
      const search = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(search) ||
          product.description.toLowerCase().includes(search) ||
          product.category.toLowerCase().includes(search)
      );
    }

    // Filtrar por categoría
    if (selectedCategory !== "all") {
      filtered = filtered.filter((product) => product.category === selectedCategory);
    }

    // Ordenar
    const sorted = [...filtered];
    switch (sortBy) {
      case "name":
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "price-asc":
        sorted.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        sorted.sort((a, b) => b.price - a.price);
        break;
      case "category":
        sorted.sort((a, b) => a.category.localeCompare(b.category));
        break;
    }

    return sorted;
  }, [products, debouncedSearchTerm, selectedCategory, sortBy]);

  // Obtener categorías únicas
  const categories = useMemo(
    () => ["all", ...new Set(products.map((p) => p.category))],
    [products]
  );

  // Loading state combinado
  const loading = loadingPOS || loadingProducts;

  const renderSkeletons = () => {
    return Array.from({ length: 8 }).map((_, index) => (
      <div
        key={index}
        className="group hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
      >
        <Card className="bg-gradient-to-br from-card to-card/80 backdrop-blur-sm border-0 shadow-lg rounded-2xl h-full">
          <CardContent className="p-4">
            <Skeleton className="h-48 w-full rounded-xl mb-4" />
            <Skeleton className="h-4 w-3/4 mb-2" />
            <Skeleton className="h-3 w-1/2 mb-2" />
            <Skeleton className="h-6 w-1/3" />
          </CardContent>
        </Card>
      </div>
    ));
  };

  const renderWeeklyMenusHero = () => {
    const { monday } = getCurrentWeekRange();
    const weekDates = getWeekDates(monday);
    const menuByFecha = weeklyMenus.reduce<Record<string, WeeklyMenu>>((acc, m) => {
      acc[m.fecha] = m;
      return acc;
    }, {});

    const scrollByAmount = (dir: "left" | "right") => {
      const el = weeklyScrollRef.current;
      if (!el) return;
      const delta = dir === "left" ? -360 : 360;
      el.scrollBy({ left: delta, behavior: "smooth" });
    };

    if (loadingMenus) {
      return (
        <div className="relative w-full">
          <div className="flex gap-4 overflow-x-auto pb-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="min-w-[260px] bg-gradient-to-br from-card to-card/80 border border-border/50 rounded-xl shadow-sm">
                <CardContent className="p-4">
                  <Skeleton className="h-40 w-full rounded-lg mb-4" />
                  <Skeleton className="h-4 w-2/3 mb-2" />
                  <Skeleton className="h-3 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-primary">
            <CalendarDays className="h-5 w-5" />
            <span className="text-sm font-medium">Menús de esta semana</span>
          </div>
          <div className="hidden sm:flex gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full bg-background/70 backdrop-blur border-border"
              onClick={() => scrollByAmount("left")}
              aria-label="Desplazar a la izquierda"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full bg-background/70 backdrop-blur border-border"
              onClick={() => scrollByAmount("right")}
              aria-label="Desplazar a la derecha"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="relative">
          {/* Botones flotantes en móviles/desktop */}
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-1 sm:hidden">
            <Button
              variant="outline"
              size="icon"
              className="pointer-events-auto h-8 w-8 rounded-full bg-background/70 backdrop-blur border-border"
              onClick={() => scrollByAmount("left")}
              aria-label="Desplazar a la izquierda"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1 sm:hidden">
            <Button
              variant="outline"
              size="icon"
              className="pointer-events-auto h-8 w-8 rounded-full bg-background/70 backdrop-blur border-border"
              onClick={() => scrollByAmount("right")}
              aria-label="Desplazar a la derecha"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div
            ref={weeklyScrollRef}
            className="flex gap-4 overflow-x-auto pb-2 scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none]"
          >
            {weekDates.map((fecha) => {
              const menu = menuByFecha[fecha];
              const dateObj = parseYYYYMMDDToLocalDate(fecha);
              const dateLabel = dateObj.toLocaleDateString("es-CL", {
                weekday: "long",
                day: "numeric",
                month: "short",
              });
              const imageUrl = (menu?.imagen_url || PLACEHOLDER_IMAGE);
              return (
                <div key={fecha} className="snap-start">
                  <div className="group min-w-[260px] rounded-xl border border-border/60 bg-gradient-to-b from-card to-card/80 shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5">
                    <div className="relative h-40 w-full overflow-hidden rounded-t-xl">
                      <img
                        src={imageUrl}
                        alt={menu ? menu.nombre : "Sin menú"}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = PLACEHOLDER_IMAGE;
                        }}
                      />
                      {/* Gradiente overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-background/10 to-transparent opacity-80"></div>
                      {/* Badge fecha */}
                      <div className="absolute top-2 left-2">
                        <span className="inline-flex items-center rounded-full bg-background/80 backdrop-blur px-2 py-1 text-[11px] font-medium text-foreground border border-border">
                          {dateLabel}
                        </span>
                      </div>
                    </div>

                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-foreground truncate pr-2">
                          {menu ? menu.nombre : "Sin menú"}
                        </h3>
                      </div>
                      {menu?.descripcion && (
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                          {menu.descripcion}
                        </p>
                      )}
                      {menu && (
                        <div className="mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => setSelectedMenuImage(menu)}
                          >
                            Ver menú
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto px-4 py-6 sm:py-8">
        {/* Header de la Tienda */}
        <div className="relative mb-8">
          {/* Fondo con gradiente y efectos */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-3xl"></div>

          <div className="relative bg-gradient-to-r from-card/80 to-card/60 backdrop-blur-xl border border-border/50 rounded-3xl p-6 sm:p-8 shadow-2xl">
            <div className="text-center mb-6">
              {/* Botón de volver al dashboard para apoderados */}
              <div className="flex justify-center mb-4">
                <BackToDashboardButton
                  variant="outline"
                  size="default"
                  className="bg-background/80 backdrop-blur-sm border-primary/30 hover:bg-primary/10"
                />
              </div>

              <div className="inline-flex items-center gap-2 mb-4">
                <Badge className="bg-primary/20 text-primary border-primary/30 px-4 py-2 rounded-full">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Tienda Online
                </Badge>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">
                Nuestros Productos
              </h1>
              <p className="text-lg text-muted-foreground">
                Descubre nuestra selección de productos de calidad para la
                comunidad escolar
              </p>
            </div>

            {/* Selector de POS destacado */}
            <div className="space-y-3 sm:space-y-4 mb-4">
              <div className="flex justify-center items-center gap-2 text-primary">
                <Store className="h-5 w-5" />
                <span className="text-sm sm:text-base font-medium">
                  Selecciona Punto de Venta
                </span>
              </div>
              <div className="relative">
                <div className="flex items-center justify-center gap-2 overflow-x-auto flex-wrap">
                  {posList.map((pos) => {
                    const isSelected = selectedPos === String(pos.id);
                    return (
                      <Button
                        key={pos.id}
                        onClick={() => {
                          setSelectedPos(String(pos.id));
                          setSelectedCategory("all");
                          setSearchTerm("");
                        }}
                        variant={isSelected ? "default" : "ghost"}
                        className={cn(
                          "rounded-full h-9 sm:h-10 px-3 sm:px-4",
                          isSelected
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "bg-transparent hover:bg-muted/60"
                        )}
                        aria-label={`Seleccionar POS ${pos.nombre}`}
                      >
                        <Store className="h-4 w-4 mr-1.5" />
                        <span className="text-sm">{pos.nombre}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Hero de menús (solo Casino) */}
            {showMenusHero && renderWeeklyMenusHero()}

            {/* Controles de búsqueda y filtros */}
            <div className="space-y-4 sm:space-y-6">
              {/* Barra de búsqueda */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar productos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12 rounded-xl border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-muted/50 rounded-lg"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Filtros y controles */}
              <div className="relative z-[3000] flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  {/* Filtro por categoría */}
                  <Select
                    value={selectedCategory}
                    onValueChange={setSelectedCategory}
                  >
                    <SelectTrigger className="w-full sm:w-48 h-12 rounded-xl border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200">
                      <SelectValue placeholder="Todas las categorías" />
                    </SelectTrigger>
                    <SelectContent className="z-[1000]">
                      {categories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category === "all"
                            ? "Todas las categorías"
                            : category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Ordenar por */}
                  <Select
                    value={sortBy}
                    onValueChange={(value) => setSortBy(value as SortOption)}
                  >
                    <SelectTrigger className="w-full sm:w-48 h-12 rounded-xl border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200">
                      <SelectValue placeholder="Ordenar por" />
                    </SelectTrigger>
                    <SelectContent className="z-[1000]">
                      <SelectItem value="name">Nombre</SelectItem>
                      <SelectItem value="price-asc">
                        Precio: Menor a Mayor
                      </SelectItem>
                      <SelectItem value="price-desc">
                        Precio: Mayor a Menor
                      </SelectItem>
                      <SelectItem value="category">Categoría</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Botones de vista */}
                <div className="flex items-center gap-2">
                  <Button
                    variant={viewMode === "grid" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("grid")}
                    className="h-10 w-10 p-0 rounded-xl hover:scale-105 transition-all duration-200"
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === "list" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setViewMode("list")}
                    className="h-10 w-10 p-0 rounded-xl hover:scale-105 transition-all duration-200"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Estadísticas */}
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {filteredProducts.length} producto
                  {filteredProducts.length !== 1 ? "s" : ""} encontrado
                  {filteredProducts.length !== 1 ? "s" : ""}
                </span>
                {searchTerm && (
                  <span className="flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" />
                    Resultados de búsqueda
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Grid de productos */}
        {loading ? (
          <div
            className={cn(
              "grid gap-4 sm:gap-6",
              viewMode === "grid"
                ? "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                : "grid-cols-1"
            )}
          >
            {renderSkeletons()}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-muted to-muted/60 rounded-full blur-xl opacity-50"></div>
              <Search className="h-16 w-16 text-muted-foreground relative z-10 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No se encontraron productos
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm
                ? `No hay productos que coincidan con "${searchTerm}"`
                : "No hay productos disponibles en esta categoría"}
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm("");
                setSelectedCategory("all");
                // Si no hay POS seleccionado, seleccionar el primero activo
                if (!selectedPos && posList.length > 0) {
                  setSelectedPos(String(posList[0].id));
                }
              }}
              className="rounded-xl hover:scale-105 transition-all duration-200"
            >
              <X className="h-4 w-4 mr-2" />
              Limpiar filtros
            </Button>
          </div>
        ) : (
          <div
            className={cn(
              "grid gap-4 sm:gap-6",
              viewMode === "grid"
                ? "grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                : "grid-cols-1"
            )}
          >
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="group hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
              >
                <ProductCard product={product} viewMode={viewMode} currentPosId={selectedPos ? Number(selectedPos) : undefined} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cart Drawer */}
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      {/* Modal de Imagen del Menú */}
      <Dialog open={!!selectedMenuImage} onOpenChange={(open) => !open && setSelectedMenuImage(null)}>
        <DialogContent className="max-w-4xl w-[95vw] p-2 sm:p-4 bg-background/95 backdrop-blur-md border-border/50">
          <DialogHeader className="px-2 pb-2">
            <DialogTitle className="text-xl">{selectedMenuImage?.nombre}</DialogTitle>
          </DialogHeader>
          <div className="relative w-full h-[70vh] rounded-md overflow-hidden bg-muted/20 flex items-center justify-center">
            {selectedMenuImage && (
              <img
                src={selectedMenuImage.imagen_url || PLACEHOLDER_IMAGE}
                alt={selectedMenuImage.nombre}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = PLACEHOLDER_IMAGE;
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
